import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Gift, QrCode, Sparkles, UserPlus } from 'lucide-react'
import { HOOFDADRES } from '../lib/adres'
import { punten } from '../lib/opmaak'
import type { Beloning } from '../lib/typen'
import { Logo } from '../onderdelen/Logo'

/* ===========================================================================
 *  De tablet als kiosk: scherm aan, screensaver, medewerkersgebaar
 *
 *  Wat een website NIET kan: het apparaat vastzetten op deze app, of de app
 *  starten als het apparaat aangaat. Dat doet het besturingssysteem
 *  (Begeleide toegang op iPad, App vastzetten of een kiosk-app op Android);
 *  zie docs/tablet-inrichten.md. Wat hier wel gebeurt:
 *
 *    - het scherm blijft aan (Screen Wake Lock), ook na terugkomen;
 *    - na een minuut niets: een screensaver die langzaam beweegt (tegen
 *      inbranden) en de camera uitzet; een tik of een scan maakt wakker;
 *    - eens per dag, als hij slaapt, laadt hij zichzelf opnieuw, zodat een
 *      nieuwe versie van de app er vanzelf op komt.
 * =========================================================================== */

interface WakeLock { release(): Promise<void>; addEventListener(t: 'release', f: () => void): void }

/** Houdt het scherm aan zolang de pagina zichtbaar is. Geeft terug of dat lukt. */
export function useSchermAan(): boolean {
  const [aan, zet] = useState(false)
  useEffect(() => {
    const wl = (navigator as unknown as { wakeLock?: { request(t: 'screen'): Promise<WakeLock> } }).wakeLock
    if (!wl) return
    let slot: WakeLock | null = null
    let weg = false
    const vraag = async () => {
      if (document.visibilityState !== 'visible' || slot) return
      try {
        slot = await wl.request('screen')
        if (weg) { slot.release(); return }
        zet(true)
        slot.addEventListener('release', () => { slot = null; zet(false) })
      } catch { zet(false) }
    }
    vraag()
    const zicht = () => { if (document.visibilityState === 'visible') vraag() }
    document.addEventListener('visibilitychange', zicht)
    /* Sommige browsers geven hem pas na een aanraking. */
    document.addEventListener('pointerdown', vraag)
    return () => {
      weg = true
      document.removeEventListener('visibilitychange', zicht)
      document.removeEventListener('pointerdown', vraag)
      slot?.release().catch(() => {})
    }
  }, [])
  return aan
}

/** true na `ms` zonder aanraking of toets, zolang `actief`. */
export function useStil(ms: number, actief: boolean): [boolean, () => void] {
  const [stil, zet] = useState(false)
  const klok = useRef<number | undefined>(undefined)
  const wek = () => {
    zet(false)
    window.clearTimeout(klok.current)
    if (actief) klok.current = window.setTimeout(() => zet(true), ms)
  }
  useEffect(() => {
    if (!actief) { zet(false); window.clearTimeout(klok.current); return }
    wek()
    const f = () => wek()
    document.addEventListener('pointerdown', f)
    document.addEventListener('keydown', f)
    return () => {
      document.removeEventListener('pointerdown', f)
      document.removeEventListener('keydown', f)
      window.clearTimeout(klok.current)
    }
  }, [actief, ms]) // eslint-disable-line react-hooks/exhaustive-deps
  return [stil, wek]
}

const GELADEN = Date.now()

export function Screensaver({ naam, beloningen, perWasbeurt, wek }: {
  naam: string; beloningen: Beloning[]; perWasbeurt: number; wek: () => void
}) {
  const regels: { i: ReactNode; t: string; s: string; foto?: string | null }[] = [
    { i: <QrCode size={34} />, t: 'Scan je kaart', s: `${punten(perWasbeurt)} per wasbeurt, op elke vestiging van Truckwash 1 Group` },
    ...beloningen.map((b) => ({ i: <Gift size={34} />, t: b.naam, s: `voor ${b.punten} punten`, foto: b.afbeelding ?? null })),
    { i: <UserPlus size={34} />, t: 'Nieuwe kaart?', s: 'Scan hem en vul je e-mailadres in. Je spaart meteen.' },
    { i: <Sparkles size={34} />, t: 'Je punten op je telefoon', s: HOOFDADRES },
  ]
  const [i, zetI] = useState(0)
  const [nu, zetNu] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => { zetI((x) => (x + 1) % regels.length); zetNu(new Date()) }, 6000)
    /* Eens per dag vers laden, terwijl er toch niemand kijkt. */
    if (Date.now() - GELADEN > 20 * 3600_000) location.reload()
    return () => clearInterval(t)
  }, [regels.length])
  const r = regels[i] ?? regels[0]!
  return (
    <div className="screensaver" onPointerDown={wek} role="button" aria-label="Tik om te beginnen">
      <div className="screensaver-drijf">
        <Logo />
        <div className="screensaver-plek">TW1 Club · {naam.replace(/^Truckwash\s*/i, '')}</div>
        <div className="screensaver-klok">{nu.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })}</div>
        <div key={i} className="screensaver-regel">
          {r.foto ? <img className="screensaver-foto" src={r.foto} alt="" /> : <span className="screensaver-icoon">{r.i}</span>}
          <b>{r.t}</b>
          <small>{r.s}</small>
        </div>
        <div className="screensaver-tik">Tik op het scherm of scan je kaart</div>
      </div>
    </div>
  )
}
